import React from 'react';

import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';

import { PostOrPage } from '@tryghost/content-api';
import { BlogPostDocument, BlogPostQuery, NavitemRecord } from 'graphql/generated';
import { Embed } from 'hyvor-talk-react';

import BlogPostHeader from 'components/BlogPostHeader';
import ContentBoxNew from 'components/ContentBoxNew';
import DatoStructuredText from 'components/DatoStructuredText';
import Footer from 'components/Footer';
import GhostContent from 'components/GhostContent';
import GhostMeta from 'components/GhostMeta';
import Navigation from 'components/Navigation';
import RootContainer from 'components/RootContainer';

import { datoRequest } from 'lib/datoSDK';
import api from 'lib/ghostSDK';
import { getBlogPostAuthors, getNavItems } from 'lib/utils';

import * as S from 'styles/Blog.styles';

interface IBlogPost {
  blogPost: PostOrPage;
  authors: {
    name: string;
    image: {
      url: string;
    };
  }[];
  blogPostCta?: BlogPostQuery['blogpostCta'];
  navItems: Array<NavitemRecord>;
}

const BlogPost = ({ blogPost, authors, blogPostCta, navItems }: IBlogPost) => {
  const router = useRouter();

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  const firstAuthor = authors[0];
  const contentBoxes = blogPostCta?.content;
  const isDjango = blogPost.tags?.filter((tag) => tag.name === 'Django').length || 0 > 0;

  return (
    <>
      <GhostMeta content={blogPost} author={firstAuthor} />

      <Navigation {...{ navItems }} />

      {isDjango &&
        contentBoxes?.map((contentBox, index) => (
          <S.ContentBoxWrapper key={index}>
            <S.ContentBoxContainer>
              <ContentBoxNew
                leftImageSrc={contentBox.leftImage?.url}
                rightImageSrc={contentBox.rightImage?.url}
                imageSize={contentBox.imageSize}
                imageMargin={contentBox.imageMargin}
              >
                <S.StyledTextBlock>
                  <DatoStructuredText data={contentBox.content} />
                </S.StyledTextBlock>
              </ContentBoxNew>
            </S.ContentBoxContainer>
          </S.ContentBoxWrapper>
        ))}

      <RootContainer>
        <BlogPostHeader
          post={blogPost}
          authors={authors}
          tags={blogPost.tags}
          publishedAt={blogPost.published_at}
        />

        <GhostContent content={blogPost}></GhostContent>

        {isDjango &&
          contentBoxes?.map((contentBox, index) => (
            <S.NotStickyContentBox key={index}>
              <ContentBoxNew
                leftImageSrc={contentBox.leftImage?.url}
                rightImageSrc={contentBox.rightImage?.url}
                imageSize={contentBox.imageSize}
                imageMargin={contentBox.imageMargin}
                marginTop={25}
              >
                <DatoStructuredText data={contentBox.content} />
              </ContentBoxNew>
            </S.NotStickyContentBox>
          ))}

        <Embed websiteId={3444} id={blogPost.id} />
      </RootContainer>

      <Footer />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const posts = await api.posts.browse({
    limit: 'all',
    fields: ['slug']
  });

  return {
    paths: posts.map((post) => ({ params: { postSlug: post.slug } })),
    fallback: false
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  if (!params) {
    return { notFound: true };
  }

  const { postSlug } = params;

  let blogPost;

  try {
    blogPost = await api.posts.read(
      {
        slug: postSlug as string
      },
      {
        include: ['authors', 'tags']
      }
    );
  } catch {
    return { notFound: true };
  }

  const blogPostQuery = await datoRequest<BlogPostQuery>(BlogPostDocument);
  const blogPostCta = blogPostQuery.blogpostCta;

  const authors = getBlogPostAuthors(blogPost, blogPostQuery.allTeamMembers);
  const navigationItemsQuery = await getNavItems();
  const navigationItemsArray = navigationItemsQuery?.allNavitems;

  return {
    props: {
      blogPost,
      authors,
      blogPostCta,
      navItems: navigationItemsArray
    },
    revalidate: false
  };
};

export default BlogPost;
